import { useCallback, useEffect, useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { FaChevronLeft } from 'react-icons/fa'
import EditableNameList from './EditableNameList'
import {
  createServiceType,
  deleteServiceType,
  getServiceTypesForAdmin,
  renameServiceType,
  ServiceTypeAdmin,
} from '../../data/lookupAdminData'
import { useToast } from '../../contexts/ToastContext'
import { getApiErrorDetails } from '../../helpers/apiError'
import './Lookups.css'

const plural = (n: number, word: string) => `${n} ${word}${n === 1 ? '' : 's'}`

/** The service types that can be chosen on a service reminder. */
const ServiceTypesAdmin = () => {
  const navigate = useNavigate()
  const { showToast } = useToast()

  const [types, setTypes] = useState<ServiceTypeAdmin[] | null>(null)
  const [loadError, setLoadError] = useState<string | null>(null)

  const load = useCallback(async () => {
    try {
      setTypes(await getServiceTypesForAdmin())
    } catch (error) {
      setLoadError(getApiErrorDetails(error, "The service types couldn't be loaded.").message)
    }
  }, [])

  useEffect(() => {
    load()
  }, [load])

  const attempt = async (work: () => Promise<unknown>, success: string, failure: string) => {
    try {
      await work()
      showToast(success, 'success')
      await load()
      return true
    } catch (error) {
      showToast(getApiErrorDetails(error, failure).message, 'error')
      return false
    }
  }

  return (
    <div className='lk-page'>
      <div className='lk-inner lk-inner--narrow'>
        <button type='button' className='lk-crumb' onClick={() => navigate('/management')}>
          <FaChevronLeft /> Management
        </button>
        <h1 className='lk-title'>Service Types</h1>
        <p className='lk-lead'>The service types that can be chosen on a service reminder.</p>

        {loadError ? (
          <div className='lk-error'>{loadError}</div>
        ) : !types ? (
          <p className='lk-muted'>Loading…</p>
        ) : (
          <section className='lk-card'>
            <p className='lk-hint'>
              Renaming one renames it on every reminder that has it. Removing one stops it being offered; reminders
              that already have it keep it.
            </p>
            <EditableNameList
              items={types.map((t) => ({
                id: t.serviceTypeId,
                name: t.name,
                meta: plural(t.reminderCount, 'reminder'),
              }))}
              addPlaceholder='New service type'
              emptyText='There are no service types yet.'
              maxLength={100}
              onAdd={(name) => attempt(() => createServiceType(name), `Added ${name}`, "The service type couldn't be added.")}
              onRename={(item, name) => attempt(() => renameServiceType(item.id, name), 'Renamed', "It couldn't be renamed.")}
              onDelete={async (item) => {
                const type = types.find((t) => t.serviceTypeId === item.id)
                const kept =
                  type && type.reminderCount > 0 ? ` The ${plural(type.reminderCount, 'reminder')} that have it will keep it.` : ''
                if (!window.confirm(`Remove "${item.name}"? It won't be offered on service reminders any more.${kept}`)) return
                await attempt(() => deleteServiceType(item.id), `Removed ${item.name}`, "It couldn't be removed.")
              }}
            />
          </section>
        )}
      </div>
    </div>
  )
}

export default ServiceTypesAdmin
